import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import { usePipelineMeta, ChecklistPill } from "@/lib/pipeline";
import { GroupTotals } from "@/components/CandidateChecklist";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const QUEUE = ["Sourcing", "Docs Verification"];

function canDrop(caps, from, to) {
  if (from === to) return false;
  if (caps.full) return true;
  if (caps.docs) return QUEUE.includes(from) && QUEUE.includes(to);
  return false;
}

function PFilter({ value, onChange, options, all, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-56 bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{all}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function MoveSelect({ c, stages, caps, onMove }) {
  const reason = (s) => (!canDrop(caps, c.current_stage, s) ? "Not allowed for your role" : c.stage_blocks?.[s]);
  if (!stages.some((s) => s !== c.current_stage && canDrop(caps, c.current_stage, s))) return null;
  return (
    <select value="" onChange={(e) => e.target.value && onMove(c, e.target.value)} className="mt-1.5 w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-600" data-testid={`kanban-move-${c.id}`}>
      <option value="">Move to stage…</option>
      {stages.filter((s) => s !== c.current_stage).map((s) => {
        const r = reason(s);
        return <option key={s} value={s} disabled={!!r} data-testid={`kanban-move-${c.id}-${s.replace(/\s+/g, "-").toLowerCase()}`}>{r ? `${s} — ${r}` : s}</option>;
      })}
    </select>
  );
}

function Card({ c, draggable, onDragStart, stages, caps, onMove }) {
  return (
    <div>
    <Link to={`/candidates/${c.id}`} draggable={draggable} onDragStart={(e) => onDragStart(e, c)}
      className={`block bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:border-cyan-400 transition-colors ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      data-testid={`kanban-card-${c.id}`}>
      <div className="font-semibold text-sm text-slate-800 truncate">{c.full_name}</div>
      <div className="text-xs text-slate-500 mt-0.5">{c.rank}</div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] font-mono text-slate-400 truncate">{c.cdc_number || "No CDC"}</span>
        <ChecklistPill done={c.checklist_done} total={c.checklist_total} testid={`kanban-checklist-${c.id}`} />
      </div>
    </Link>
    {caps.is_sourcing && !caps.full && <div className="mt-1.5"><GroupTotals groups={c.groups} testid={`kanban-group-totals-${c.id}`} /></div>}
    <MoveSelect c={c} stages={stages} caps={caps} onMove={onMove} />
    </div>
  );
}

export default function PipelinePage() {
  const meta = usePipelineMeta();
  const [items, setItems] = useState(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const load = () => api.get("/candidates").then(({ data }) => setItems(data)).catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { load(); }, []);
  const [fr, setFr] = useState("all");
  const [fv, setFv] = useState("all");

  if (!meta || !items) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>;
  const caps = meta.caps;
  const draggableFrom = (stage) => caps.full || (caps.docs && QUEUE.includes(stage));

  const move = async (c, stage) => {
    setItems(items.map((x) => (x.id === c.id ? { ...x, current_stage: stage } : x)));
    try { await api.post(`/candidates/${c.id}/stage`, { stage }); toast.success(`${c.full_name} → ${stage}`); } catch (err) { toast.error(formatApiError(err)); }
    load();
  };
  const drop = async (stage) => {
    setOver(null);
    const c = drag;
    setDrag(null);
    if (!c || !canDrop(caps, c.current_stage, stage)) {
      if (c && c.current_stage !== stage) toast.error("Your role cannot move candidates to that stage");
      return;
    }
    setItems(items.map((x) => (x.id === c.id ? { ...x, current_stage: stage } : x)));
    try { await api.post(`/candidates/${c.id}/stage`, { stage }); toast.success(`${c.full_name} → ${stage}`); } catch (err) { toast.error(formatApiError(err)); }
    load();
  };

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Pipeline</div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Candidate Pipeline</h1>
        <div className="text-sm text-slate-500 mt-1">{caps.full ? "Drag cards to move candidates between stages." : caps.docs ? "You can move cards between Sourcing and Docs Verification." : "View only."}</div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="pipeline-filters">
        <PFilter value={fr} onChange={setFr} all="All ranks" options={meta.ranks} testid="pipeline-filter-rank" />
        <PFilter value={fv} onChange={setFv} all="All vessel types" options={meta.vessel_types} testid="pipeline-filter-vessel-type" />
      </div>
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="kanban-board">
        {meta.stages.map((s) => {
          const cards = items.filter((c) => c.current_stage === s && (fr === "all" || (fr === "Others" ? c.ranks?.some((r) => r.startsWith("Others: ")) : c.ranks?.includes(fr))) && (fv === "all" || c.vessel_types?.includes(fv)));
          const ok = drag && canDrop(caps, drag.current_stage, s);
          return (
            <div key={s} onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(ok ? s : null); } }} onDragLeave={() => setOver(null)} onDrop={() => drop(s)}
              className={`w-64 shrink-0 rounded-xl border p-2.5 transition-colors ${over === s ? "bg-cyan-50 border-cyan-300" : "bg-slate-100/70 border-slate-200"} ${drag && !ok && drag.current_stage !== s ? "opacity-50" : ""}`}
              data-testid={`kanban-col-${s}`}>
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{s}</span>
                <span className="text-xs font-mono text-slate-400" data-testid={`kanban-count-${s}`}>{cards.length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {cards.map((c) => <Card key={c.id} c={c} stages={meta.stages} caps={caps} onMove={move} draggable={draggableFrom(s)} onDragStart={(e, x) => { e.dataTransfer.effectAllowed = "move"; setDrag(x); }} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
